import { useTranslation } from 'react-i18next';
import { Edit3Icon, MapPinIcon, Trash2Icon } from '@/shared/ui/icons';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import type { ApiAddressesGetItem } from '@/shared/types/api/addresses';

type Props = {
  address: ApiAddressesGetItem;
  onEdit: (address: ApiAddressesGetItem) => void;
  onDelete: (address: ApiAddressesGetItem) => void;
};

export const FeaturesAddressesAddressCard = ({ address, onEdit, onDelete }: Props) => {
  const { t } = useTranslation('addresses');
  const summary = [
    address.address_name,
    t(`regions:${address.region}`),
    address.city,
    address.street,
    address.building,
    address.apartment,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <article className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
      <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
        <MapPinIcon className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-base font-semibold text-ink">{address.label}</p>
          {address.is_default && (
            <span className="shrink-0 rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-700">
              {t('defaultBadge')}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-ink-muted">{summary}</p>
        {address.additional_info && (
          <p className="mt-1 text-xs text-ink-faint">{address.additional_info}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <IconButton label={t('edit')} variant="ghost" size="sm" onClick={() => onEdit(address)}>
          <Edit3Icon className="h-4 w-4" />
        </IconButton>
        <IconButton label={t('delete')} variant="ghost" size="sm" onClick={() => onDelete(address)}>
          <Trash2Icon className="h-4 w-4 text-red-600" />
        </IconButton>
      </div>
    </article>
  );
};
